// src/views/MetricsDashboard/hooks/useDashboardData.js
// Custom hooks para el Dashboard Avanzado de SegoCloud

import { useState, useEffect, useCallback } from 'react';
import { postApi } from 'views/services/api';
import { toast } from 'react-toastify';

/**
 * Hook para manejar los datos del dashboard
 * CORREGIDO: useEffect ahora detecta correctamente los cambios de fecha
 */
export const useDashboardData = (initialStartDate, initialEndDate) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  // Efecto para sincronizar el estado interno cuando cambian las props iniciales
  useEffect(() => {
    if (initialStartDate) setStartDate(initialStartDate);
    if (initialEndDate) setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  // Efecto que se ejecuta cuando cambian las fechas internal
  useEffect(() => {
    if (!startDate || !endDate) {
      console.log('⚠️ Fechas no definidas, esperando...');
      return;
    }

    console.log('🔄 useEffect disparado - Nuevas fechas:', { startDate, endDate });

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const user = JSON.parse(localStorage.getItem('hotelData'));
        const hotelId = user._id;

        console.log('🏨 Hotel ID:', hotelId);
        console.log('📅 Fechas a enviar:', { startDate, endDate });
        console.log('📤 Request body completo:', { hotelId, startDate, endDate });

        const response = await postApi('api/dashboard/metrics', {
          hotelId,
          startDate,
          endDate
        });

        console.log('📥 Respuesta del backend:', response);

        if (response.status === 200) {
          console.log('✅ Datos recibidos correctamente');
          setData(response.data);
        } else {
          throw new Error(response?.response?.data?.error || 'Error al obtener datos');
        }
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message || 'Error al obtener los datos';
        console.error('❌ Error completo:', err);
        console.error('❌ Error mensaje:', errorMsg);
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]); // ← Solo fechas como dependencias

  // Función para refrescar datos manualmente
  const refetch = useCallback(() => {
    console.log('🔄 Refetch manual disparado');
    console.log('📅 Fechas actuales:', { startDate, endDate });

    // Forzar re-fetch cambiando el estado
    const tempDate = startDate;
    setStartDate('');
    setTimeout(() => setStartDate(tempDate), 0);
  }, [startDate, endDate]);

  return {
    data,
    loading,
    error,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    refetch
  };
};

/**
 * Hook para manejar filtros de fecha con presets
 * CORREGIDO: Inicializa con "yesterday" por defecto
 */
import { getHotelMoment, reportDateHelpers } from 'utils/timeZone';

export const useDateFilter = (initialStart, initialEnd) => {
  // Calcular "today" como valor inicial usando la zona horaria del hotel
  const getToday = () => {
    return getHotelMoment().format('YYYY-MM-DD');
  };

  const [startDate, setStartDate] = useState(initialStart || getToday());
  const [endDate, setEndDate] = useState(initialEnd || getToday());
  const [preset, setPreset] = useState('yesterday'); // Se mantiene 'yesterday' por defecto según requerimiento anterior, o 'today' si se prefiere

  // Inicializar hook con preset por defecto
  useEffect(() => {
    // Si no se pasaron fechas iniciales, aplicar preset por defecto al montar
    if (!initialStart && !initialEnd) {
      applyPreset('today'); // Cambiado a 'today' para coincidir con la lógica general, o 'yesterday' si es explícito
    }
  }, []);

  const applyPreset = useCallback((presetName) => {
    const now = getHotelMoment();
    let start, end;

    console.log('🎯 Aplicando preset (Hotel TZ):', presetName);

    switch (presetName) {
      case 'today': {
        start = now.clone();
        end = now.clone();
        break;
      }
      case 'yesterday': {
        const yesterday = now.clone().subtract(1, 'day');
        start = yesterday.clone();
        end = yesterday.clone();
        break;
      }
      case 'thisWeek': {
        // Semana de Lunes a Domingo
        start = now.clone().startOf('isoWeek');
        end = now.clone().endOf('isoWeek');
        break;
      }
      case 'thisMonth': {
        start = now.clone().startOf('month');
        end = now.clone().endOf('month');
        break;
      }
      case 'thisYear': {
        start = now.clone().startOf('year');
        end = now.clone().endOf('year');
        break;
      }
      case 'last7days': {
        end = now.clone();
        start = now.clone().subtract(6, 'days');
        break;
      }
      case 'last30days': {
        end = now.clone();
        start = now.clone().subtract(29, 'days');
        break;
      }
      case 'lastMonth': {
        const lastMonth = now.clone().subtract(1, 'month');
        start = lastMonth.clone().startOf('month');
        end = lastMonth.clone().endOf('month');
        break;
      }
      case 'lastYear': {
        const lastYear = now.clone().subtract(1, 'year');
        start = lastYear.clone().startOf('year');
        end = lastYear.clone().endOf('year');
        break;
      }
      default:
        console.warn('⚠️ Preset desconocido:', presetName);
        return;
    }

    const startStr = start.format('YYYY-MM-DD');
    const endStr = end.format('YYYY-MM-DD');

    console.log('📅 Nuevas fechas calculadas (Hotel TZ):', { start: startStr, end: endStr });

    setStartDate(startStr);
    setEndDate(endStr);
    setPreset(presetName);
  }, []);

  const setCustomRange = useCallback((start, end) => {
    console.log('🔄 Rango personalizado:', { start, end });
    setStartDate(start);
    setEndDate(end);
    setPreset('custom');
  }, []);

  const updateStartDate = useCallback((date) => {
    setStartDate(date);
    setPreset('custom');
  }, []);

  const updateEndDate = useCallback((date) => {
    setEndDate(date);
    setPreset('custom');
  }, []);

  return {
    startDate,
    endDate,
    preset,
    setStartDate: updateStartDate,
    setEndDate: updateEndDate,
    applyPreset,
    setCustomRange
  };
};

/**
 * Hook para exportar datos
 */
export const useExportData = () => {
  const exportToCSV = useCallback((data, filename) => {
    try {
      const csvContent = convertToCSV(data);
      downloadFile(csvContent, filename, 'text/csv');
      toast.success('Datos exportados exitosamente');
    } catch (error) {
      toast.error('Error al exportar datos');
      console.error('Export error:', error);
    }
  }, []);

  const exportToJSON = useCallback((data, filename) => {
    try {
      const jsonContent = JSON.stringify(data, null, 2);
      downloadFile(jsonContent, filename, 'application/json');
      toast.success('Datos exportados exitosamente');
    } catch (error) {
      toast.error('Error al exportar datos');
      console.error('Export error:', error);
    }
  }, []);

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';

    const headers = Object.keys(data[0]).join(',');
    const rows = data.map((row) =>
      Object.values(row)
        .map((val) => (typeof val === 'string' && val.includes(',') ? `"${val}"` : val))
        .join(',')
    );
    return [headers, ...rows].join('\n');
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return {
    exportToCSV,
    exportToJSON
  };
};

/**
 * Hook para auto-refresh del dashboard
 */
export const useAutoRefresh = (callback, interval = 300000) => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!isActive || !callback) return;

    console.log('🔄 Auto-refresh activado - Intervalo:', interval, 'ms');

    const intervalId = setInterval(() => {
      console.log('🔄 Auto-refresh ejecutando callback');
      callback();
    }, interval);

    return () => {
      console.log('🛑 Auto-refresh desactivado');
      clearInterval(intervalId);
    };
  }, [isActive, callback, interval]);

  const start = useCallback(() => {
    console.log('▶️ Auto-refresh iniciado');
    setIsActive(true);
  }, []);

  const stop = useCallback(() => {
    console.log('⏸️ Auto-refresh detenido');
    setIsActive(false);
  }, []);

  const toggle = useCallback(() => {
    console.log('🔄 Auto-refresh toggle');
    setIsActive((prev) => !prev);
  }, []);

  return {
    isActive,
    start,
    stop,
    toggle
  };
};

/**
 * Hook para detectar tamaño de pantalla
 */
export const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        isMobile: width < 600,
        isTablet: width >= 600 && width < 960,
        isDesktop: width >= 960
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return screenSize;
};

/**
 * Hook para manejar alertas/notificaciones
 */
export const useDashboardAlerts = () => {
  const [alerts, setAlerts] = useState([]);

  const addAlert = useCallback((message, severity = 'info', duration = 5000) => {
    const id = Date.now();
    console.log('🔔 Nueva alerta:', { id, message, severity });
    setAlerts((prev) => [...prev, { id, message, severity }]);

    if (duration > 0) {
      setTimeout(() => {
        removeAlert(id);
      }, duration);
    }
  }, []);

  const removeAlert = useCallback((id) => {
    console.log('❌ Removiendo alerta:', id);
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const clearAlerts = useCallback(() => {
    console.log('🗑️ Limpiando todas las alertas');
    setAlerts([]);
  }, []);

  return {
    alerts,
    addAlert,
    removeAlert,
    clearAlerts
  };
};

// Exportación por defecto
export default {
  useDashboardData,
  useDateFilter,
  useExportData,
  useAutoRefresh,
  useResponsive,
  useDashboardAlerts
};
